import { FC, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { StyledCrossIcon } from '../../../icons/CrossIcon'
import { StyledSearchIcon } from '../../../icons/SearchIcon'
import { ArticleStandard } from '../../article/components/ArticleStandard'
import {
    clearSearchedArticles,
    selectSearchedArticles,
    selectSearchedArticlesStatus,
    selectSearchText,
    setSearchedArticlesStatusToLoading,
    setSearchText,
} from '../../search/searchSlice'
import { RequestContentButton } from '../styled/RequestContentButton'
import { SearchInput } from '../styled/SearchInput'
import { SearchInputContainer } from '../styled/SearchInputContainer'
import { fetchArticlesBySearchThunk } from '../../search/searchSlice'
import { useSearchParams } from 'react-router-dom'
import { StyledNoSearchResultsIcon } from '../../../icons/NoSearchResultsIcon'
import { StyledNoSearchResults } from '../styled/StyledNoSearchResults'
import { StyleSearchLoading } from '../styled/StyledSearchLoading'
import { selectCurrentlyPlayingArticle } from '../../article/data/articleSlice'

const SearchArticles: FC = () => {
    const dispatch = useAppDispatch()
    const clearSearchText = () => dispatch(setSearchText(''))
    const onSearchTextChange: React.ChangeEventHandler<HTMLInputElement> = e => dispatch(setSearchText(e.target.value))
    const searchText = useSelector(selectSearchText)
    const currentlyPlayingArticle = useAppSelector(selectCurrentlyPlayingArticle)
    const searchedArticlesStatus = useAppSelector(selectSearchedArticlesStatus)
    const [searchParams] = useSearchParams()

    const showChat = () => {
        window.Intercom('show')
    }

    const dispatchFetchArticles = (text: string) => {
        const publisherKey = searchParams.get('key')
        if (publisherKey && publisherKey.length) {
            dispatch(fetchArticlesBySearchThunk({ publisherKey: publisherKey, text: text, currentlyPlayingArticle }))
        }
    }

    useEffect(() => {
        if (!!searchText.trim().length) {
            dispatch(setSearchedArticlesStatusToLoading())
            dispatchFetchArticles(searchText)
        } else {
            dispatch(clearSearchedArticles())
        }
    }, [searchText, searchParams])

    return (
        <>
            <SearchInputContainer isSearchText={!!searchText.length}>
                <SearchInput value={searchText} onChange={onSearchTextChange} placeholder='Search for audio articles' />
                <span>
                    <StyledSearchIcon size='26px' />
                </span>
                <button onClick={clearSearchText}>
                    <StyledCrossIcon />
                </button>
            </SearchInputContainer>

            <RenderSearchResult retrySearch={() => dispatchFetchArticles(searchText)} />

            {!!searchText.trim().length && searchedArticlesStatus === 'idle' && (
                <RequestContentButton onClick={showChat}>Request Content</RequestContentButton>
            )}
        </>
    )
}

const RenderSearchResult: FC<{ retrySearch: () => void }> = ({ retrySearch }) => {
    const searchedArticlesStatus = useAppSelector(selectSearchedArticlesStatus)
    const searchedArticles = useAppSelector(selectSearchedArticles)
    const searchText = useSelector(selectSearchText)

    switch (searchedArticlesStatus) {
        case 'idle':
            return searchedArticles.length && !!searchText.trim().length ? (
                <>
                    {searchedArticles.map(article => {
                        return <ArticleStandard key={article.id} article={article} />
                    })}
                </>
            ) : !!searchText.trim().length ? (
                <StyledNoSearchResults>
                    <div>
                        <StyledNoSearchResultsIcon />
                    </div>
                    <p>
                        No results found.
                        <br />
                        Try something else.
                    </p>
                </StyledNoSearchResults>
            ) : null

        case 'pending':
            return <StyleSearchLoading>Loading..</StyleSearchLoading>

        case 'failed':
            return (
                <div>
                    <h1>Failed!</h1>
                    <br />
                    <button onClick={retrySearch}>Retry</button>
                </div>
            )

        default:
            return null
    }
}

export default SearchArticles
